import { siteTexts } from "../../../shared/i18n/siteTexts";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import type {
  ChallengeGameType,
  ChallengeModel,
  ChallengeType,
} from "../../challenges/types/challengeAdmin";

type CustomSettingsValue = {
  challengeModel?: ChallengeModel | null;
  gameType?: ChallengeGameType | null;
  gameCount?: number | string | null;
  gameDurationMinutes?: number | string | null;
  courtsCount?: number | string | null;
  playersCount?: number | string | null;
};

type AdminChallengeCustomSettingsFieldsProps = {
  challengeType: ChallengeType;
  value: CustomSettingsValue;
  onChange: (patch: CustomSettingsValue) => void;
};

export default function AdminChallengeCustomSettingsFields({
  challengeType,
  value,
  onChange,
}: AdminChallengeCustomSettingsFieldsProps) {
  const texts = siteTexts.admin.createChallengePage.customSettings;
  const info = siteTexts.common.info;
  const model = value.challengeModel === "custom" ? "custom" : "tnp_standard";
  const gameType = value.gameType === "time" ? "time" : "games";
  const isSuper8 = challengeType === "super8";
  const isPoco = challengeType === "poco";
  const isCustom = model === "custom";
  const courtsCount = isPoco
    ? Math.floor(Number(value.courtsCount)) === 4
      ? 4
      : 3
    : Math.max(2, Math.floor(Number(value.courtsCount || 2)));
  const pocoPlayersCount = courtsCount * 4;
  const playersCountValue = isPoco
    ? pocoPlayersCount
    : (value.playersCount ?? 12);
  const playersHelpText = isPoco
    ? texts.pocoExactPlayersHelp
    : texts.challengeWaitingPlayersHelp;

  return (
    <div className="admin-row-two">
      <label className="field">
        <span className="info-label">
          {texts.challengeModel}
          <InfoTooltip content={info.challengeModel} />
        </span>
        <select
          value={model}
          onChange={(event) => {
            const next = event.target.value as ChallengeModel;
            onChange({
              challengeModel: next,
              ...(next === "custom"
                ? {
                    gameType: value.gameType || "games",
                    playersCount: isSuper8
                      ? 8
                      : isPoco
                        ? pocoPlayersCount
                        : value.playersCount || 12,
                    courtsCount: isSuper8 ? null : courtsCount,
                  }
                : {
                    gameType: null,
                    gameCount: null,
                    gameDurationMinutes: null,
                    playersCount: isPoco ? pocoPlayersCount : null,
                    courtsCount: isPoco ? courtsCount : null,
                  }),
            });
          }}
        >
          <option value="tnp_standard">{texts.tnpStandard}</option>
          <option value="custom">{texts.custom}</option>
        </select>
      </label>

      {isCustom && !isPoco && !isSuper8 && (
        <>
          <label className="field">
            <span className="info-label">
              {texts.playersCount}
              <InfoTooltip content={info.playersCount} />
            </span>
            <input
              type="number"
              min={12}
              step={1}
              value={playersCountValue}
              readOnly={isPoco}
              disabled={isPoco}
              onChange={(event) => {
                if (isPoco) return;
                onChange({ playersCount: event.target.value });
              }}
            />
            <small>{playersHelpText}</small>
          </label>
          <label className="field">
            <span className="info-label">
              {texts.courtsCount}
              <InfoTooltip content={info.courtsCount} />
            </span>
            <input
              type="number"
              min={2}
              step={1}
              value={value.courtsCount ?? courtsCount}
              onChange={(event) =>
                onChange({ courtsCount: event.target.value })
              }
            />
          </label>
        </>
      )}

      {isCustom && isSuper8 && (
        <label className="field">
          <span className="info-label">
            {texts.playersCount}
            <InfoTooltip content={info.playersCount} />
          </span>
          <input type="number" value={8} disabled readOnly />
          <small>{siteTexts.admin.validation.super8ParticipantsRequired}</small>
        </label>
      )}

      {isCustom && (
        <>
          <label className="field">
            <span className="info-label">
              {texts.gameType}
              <InfoTooltip content={info.gameType} />
            </span>
            <select
              value={gameType}
              onChange={(event) =>
                onChange({ gameType: event.target.value as ChallengeGameType })
              }
            >
              <option value="games">{texts.byGames}</option>
              <option value="time">{texts.byTime}</option>
            </select>
          </label>
          {gameType === "games" ? (
            <label className="field">
              <span className="info-label">
                {texts.gameCount}
                <InfoTooltip content={info.gameCount} />
              </span>
              <input
                type="number"
                min={1}
                step={1}
                value={value.gameCount ?? ""}
                onChange={(event) => onChange({ gameCount: event.target.value })}
              />
            </label>
          ) : (
            <label className="field">
              <span className="info-label">
                {texts.gameDurationMinutes}
                <InfoTooltip content={info.gameDuration} />
              </span>
              <input
                type="number"
                min={1}
                step={1}
                value={value.gameDurationMinutes ?? ""}
                onChange={(event) =>
                  onChange({ gameDurationMinutes: event.target.value })
                }
              />
            </label>
          )}
        </>
      )}
    </div>
  );
}
